import { Component, Inject, ViewChild, ElementRef, HostListener, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { SnackBarService } from '@app/services/snack-bar.service';
import { ConfirmService } from '@app/services/confirm.service';
import { SubService } from '@app/services/sub.service';
import { DialogImagemData } from '@app/models/dialog-image-data';

@Component({
    selector: 'app-dialog-imagem',
    templateUrl: './dialog-imagem.component.html',
    styleUrls: ['./dialog-imagem.component.css']
})
export class DialogImagemComponent implements OnInit {
    @ViewChild('fileInput') public fileInput!: ElementRef;
    public imagemSelecionada: File | null = null;
    public imagemPreview: string | null = null;
    public imagemMinio: string | null = null;
    public isDragging = false;

    public constructor(
        public dialogRef: MatDialogRef<DialogImagemComponent>,
        @Inject(MAT_DIALOG_DATA) public data: DialogImagemData,
        private httpClient: HttpClient,
        private sub: SubService,
        private snackBarService: SnackBarService,
        private confirmService: ConfirmService
    ) {
        if (data.imagemMinio) {
            this.imagemPreview = data.imagemMinio;
        }
        this.dialogRef.disableClose = true;
    }

    public ngOnInit(): void {
        document.addEventListener('dragover', (e) => e.preventDefault(), false);
        document.addEventListener('drop', (e) => e.preventDefault(), false);
    }

    public onDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'copy';
        }
        this.isDragging = true;
    }

    public onDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = false;
    }

    public onDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = false;

        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (file.type.startsWith('image/')) {
                this.processImageFile(file);
            } else {
                this.snackBarService.exibirErro('Por favor, solte apenas arquivos de imagem.');
            }
        }
    }

    @HostListener('document:paste', ['$event'])
    public onPaste(event: ClipboardEvent): void {
        if (this.data.isVisualizacao || this.imagemPreview || this.data.imagemMinio) {
            return;
        }

        const items = event.clipboardData?.items;
        if (items) {
            for (let i = 0; i < items.length; i++) {
                if (items[i].type.startsWith('image/')) {
                    const file = items[i].getAsFile();
                    if (file) {
                        event.preventDefault();
                        this.processImageFile(file);
                        return;
                    }
                }
            }
        }
    }

    public onFileSelected(event: any): void {
        const file = event.target.files[0];
        if (file) {
            if (file.type.startsWith('image/')) {
                this.processImageFile(file);
                this.data.nomeArquivo = file.name;
            } else {
                this.snackBarService.exibirErro('Por favor, selecione apenas arquivos de imagem.');
                this.fileInput.nativeElement.value = '';
            }
        }
    }

    public onAnexar(): void {
        if (!this.imagemSelecionada) {
            this.snackBarService.exibirErro('Por favor, selecione uma imagem para anexar.');
            return;
        }

        const formData = new FormData();
        formData.append('imagem', this.imagemSelecionada);
        formData.append('idControleFinancaMovimento', this.data.idControleFinancaMovimento.toString());

        this.httpClient.post('/api/ControleFinancaMovimentos/AnexarImagem', formData)
            .subscribe({
                next: () => {
                    this.snackBarService.exibirSucesso('Imagem anexada com sucesso!');
                    this.dialogRef.close(true);
                },
                error: (error) => {
                    this.snackBarService.exibirErro('Erro ao anexar a imagem: ' + error.message);
                }
            });
    }

    public onDownload(): void {
        if (!this.data.imagemMinio) {
            this.snackBarService.exibirErro('Nenhuma imagem disponível para download.');
            return;
        }

        this.httpClient.get(`/api/ControleFinancaMovimentos/DownloadImagem/${this.data.idControleFinancaMovimento}`,
            { responseType: 'blob' })
            .subscribe({
                next: (blob: Blob) => {
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${this.data.nomeArquivo}`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                },
                error: (error) => {
                    this.snackBarService.exibirErro('Erro ao baixar a imagem: ' + error.message);
                }
            });
    }

    public onDelete() {
        this.confirmService.confirm('Deseja excluir esse registro?', 'Alerta', 'Excluir')
            .subscribe(res => {
                if (res) {
                    this.sub.reset(this.httpClient.delete(`/api/ControleFinancaMovimentos/RemoverImagem/${this.data.idControleFinancaMovimento}`),
                        () => {
                            this.snackBarService.exibirSucesso('Registro excluído!');
                            this.imagemPreview = null;
                            this.imagemSelecionada = null;
                            this.data.isVisualizacao = false;
                            this.data.imagemMinio = undefined;
                            this.dialogRef.close('deleted');
                        },
                        () => this.snackBarService.exibirErro('Não foi possível excluir o registro!')
                    );
                }
            });
    }

    public onCancel(): void {
        this.dialogRef.close();
    }
    
    public onAbrirNovaAba() {
        if (this.data.imagemMinio) {
            if (this.data.imagemMinio.startsWith('data:')) {
                const byteString = atob(this.data.imagemMinio.split(',')[1]);
                const mimeString = this.data.imagemMinio.split(',')[0].split(':')[1].split(';')[0];
                const ab = new ArrayBuffer(byteString.length);
                const ia = new Uint8Array(ab);
                for (let i = 0; i < byteString.length; i++) {
                    ia[i] = byteString.charCodeAt(i);
                }
                const blob = new Blob([ab], { type: mimeString });
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 100);
            } else {
                window.open(this.data.imagemMinio, '_blank');
            }
        } else {
            this.snackBarService.exibirErro('Nenhuma imagem disponível para visualização.');
        }
    }

    private processImageFile(file: File): void {
        this.imagemSelecionada = file;
        const reader = new FileReader();
        reader.onload = () => {
            this.imagemPreview = reader.result as string;
        };
        reader.readAsDataURL(file);
    }

}
